import React, { useMemo, useRef } from "react";

import { getTranslationValue } from "../../services";
import { Paragraph } from "../atoms";
import { Flex } from "../cores";
import {
  useGetSdlConfig,
  useGetTranslationConfig,
  useViewContext,
} from "../hooks";
import { IconArrowDown, IconArrowUp } from "../icons";
import { Table } from "../molecules";
import { sortTable } from "../molecules/Table/SortTable";
import { useTableContext } from "../providers";
import { IKeyStringAndValue, IOptions } from "../types/Types";
import Pagination from "./Pagination";

interface ITableWithPages {
  data: Array<IKeyStringAndValue>;
}

const TableWithPages = ({ data }: ITableWithPages) => {
  const { listView } = useTableContext();
  const translations = useGetTranslationConfig(listView)
    .data as Array<IKeyStringAndValue>;
  const configData = useGetSdlConfig(listView).data as Array<IOptions>;
  const {
    sortedField,
    filteredData,
    setFilteredData,
    currentPage,
    setCurrentPage,
    arrowState,
    setArrowState,
    selected,
    setSelected,
    setSortedField,
    filterOptions,
  } = useTableContext();
  const { isMobile } = useViewContext();
  const pageSize = (): number => {
    //Gets the page size from the config if found otherwise it sets page size to 10
    const pageSize = configData.find(
      (element) => element.label === "Page_Size"
    );
    return pageSize ? Number(pageSize.key) : 10;
  };

  const emptyFilterOptions = Object.keys(filterOptions || {}).length === 0;

  //Takes out columns from SDL for use later with matching order with the table data
  const columns = useMemo(() => {
    return configData.reduce((acc, element) => {
      const arr = [
        "boras_betyg",
        "uppsala_betyg",
        "vindeln_betyg",
        "alnarp_betyg",
        "funktion",
      ];
      if (arr.some((a) => element.key === a)) {
        element.hideColumn = true;
      }
      if ((filterOptions as IKeyStringAndValue)["omrade"] === "Boras") {
        if (element.key === "boras_betyg") {
          element.hideColumn = false;
        }
      } else if (
        (filterOptions as IKeyStringAndValue)["omrade"] === "Uppsala"
      ) {
        if (element.key === "uppsala_betyg") {
          element.hideColumn = false;
        }
      } else if (
        (filterOptions as IKeyStringAndValue)["omrade"] === "Vindeln (Umeå)"
      ) {
        if (element.key === "vindeln_betyg") {
          element.hideColumn = false;
        }
      } else if (
        (filterOptions as IKeyStringAndValue)["omrade"] === "Alnarp (Malmö)"
      ) {
        if (element.key === "alnarp_betyg") {
          element.hideColumn = false;
        }
      } else if (
        (filterOptions as IKeyStringAndValue)["omrade"] === "Hela Sverige" ||
        emptyFilterOptions
      ) {
        if (element.key === "funktion") {
          element.hideColumn = false;
        }
      }
      if (!element.hideColumn) acc = { ...acc, [element.key]: element.label };
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, filterOptions]);
  //Setup for pagination
  const currentTableData = useMemo(() => {
    const firstPageIndex = ((currentPage as number) - 1) * pageSize();
    const lastPageIndex = firstPageIndex + pageSize();
    return filteredData.slice(firstPageIndex, lastPageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filteredData]);

  const tableButtonRef = useRef(
    Object.keys(data[0]).map(() => React.createRef())
  );
  const handleClick = (columnKey: string): void => {
    sortTable(
      columnKey,
      filteredData,
      setFilteredData,
      sortedField !== columnKey && !arrowState ? !arrowState : arrowState,
      setCurrentPage
    );
    setArrowState(
      sortedField !== columnKey && !arrowState
        ? (prevArrowState) => prevArrowState
        : (prevArrowState) => !prevArrowState
    );
    setSortedField(columnKey);
  };

  return (
    <Flex
      sx={{
        flexDirection: "column",
        marginBottom: 44,
        marginX: isMobile ? 0 : 16,
        justifyContent: "center",
        borderRadius: "12px",
        boxShadow:
          "0px 2px 5px -1px rgba(50, 50, 93, 0.25), 0px 1px 3px -1px rgba(0, 0, 0, 0.3)",

        overflow: "hidden",
        "> div::after": {
          zIndex: 9,
        },
        "> div > div > table": {
          height: "100%",
          "> * > * > *": {
            width: "1%",
            verticalAlign: "middle !important",
            "> *": {
              height: "100%",
            },
          },
          "> tbody > tr > td:first-of-type": {
            width: 100000,
          },
          "> thead > tr >  th:first-of-type > div > button": {
            borderRadius: "12px 0 0 0",
          },
          "> thead > tr > th:last-of-type > div > button": {
            borderRadius: "0 12px 0 0",
          },
          "> thead > tr > th:first-of-type": { borderRadius: "12px 0 0 0" },
          "> thead > tr > th:last-of-type": {
            borderRadius: "0 12px 0 0",
          },
        },
      }}
    >
      {currentTableData.length > 0 && Array.isArray(currentTableData) ? (
        <Table isMobile>
          <Table.Header>
            <Table.Row>
              {Object.keys(data[0])
                .sort(
                  (a, b) =>
                    Object.keys(columns).indexOf(a) -
                    Object.keys(columns).indexOf(b)
                )
                .map((column, index) => {
                  const colHeaderName = Object.values(columns).filter(
                    (_, index) => {
                      return Object.keys(columns)[index] === column;
                    }
                  )[0];
                  if (typeof colHeaderName === "string")
                    return (
                      <Table.HeaderColumn key={column}>
                        <Table.Button
                          //@ts-ignore
                          ref={tableButtonRef.current[index]}
                          iconRight={
                            sortedField === column && arrowState !== null
                              ? arrowState
                                ? IconArrowUp
                                : IconArrowDown
                              : null
                          }
                          index={index}
                          text={colHeaderName}
                          onClick={() => handleClick(column)}
                        />
                      </Table.HeaderColumn>
                    );
                  else return null;
                })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {currentTableData.map((row: IKeyStringAndValue, idx: number) => {
              const cols = configData.reduce(
                (acc: Array<Array<string>>, element: IOptions) => {
                  if (
                    Object.keys(row).includes(element.key) &&
                    !element.hideColumn
                  ) {
                    acc = [
                      ...acc,
                      [
                        element.key,
                        getTranslationValue(
                          row[element.key],
                          element.key,
                          translations
                        ),
                      ],
                    ];
                  }
                  return acc;
                },
                []
              );
              return (
                <Table.Row key={idx}>
                  {cols.map((element: Array<string>, index: number) => {
                    return (
                      <Table.ColumnCustom
                        filteredData={filteredData}
                        key={index}
                        element={element}
                        index={index}
                        selected={selected}
                        setSelected={setSelected}
                        currentPage={currentPage}
                        PageSize={pageSize()}
                        rowIndex={idx}
                        filterOptions={filterOptions}
                      />
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <Flex
          sx={{
            padding: 20,
            backgroundColor: "quinaryScale.2",
            justifyContent: "center",
            width: "100%",
            boxShadow:
              "0px 2px 5px -1px rgba(50, 50, 93, 0.25), 0px 1px 3px -1px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Paragraph sx={{ textAlign: "center", fontSize: 20 }}>
            Resultatet gav inga matchingar!
          </Paragraph>
        </Flex>
      )}
      <Pagination
        currentPage={currentPage}
        totalCount={filteredData.length}
        pageSize={pageSize()}
        onPageChange={(page: number | string) => setCurrentPage(page)}
      />
    </Flex>
  );
};

export default TableWithPages;
